"use client"

import { useId, useMemo, useState } from "react"
import { useDataTableStore } from "@/stores/data-table-store"
import { zodResolver } from "@hookform/resolvers/zod"
import { SelectField, TextField } from "@ui/forms"
import { Button, Checkbox, Form, FormField, Input, Label } from "@ui/shad"
import { Loader2 } from "lucide-react"
import { useForm } from "react-hook-form"
import { toast } from "sonner"
import * as z from "zod"

import { useCreateCustomField } from "@/hooks/api/custom_fields"
import { useFetchUsers } from "@/hooks/api/users"
import { buildCustomFieldColumn } from "@/hooks/use-columns-with-custom-fields"
import { ColorPicker } from "@/components/_ui/color-picker"
import { SimpleFormDialog } from "@/components/dialogs"
import { CROSS_REFERENCE_TARGET_OPTIONS } from "@/components/settings/custom-fields/constants"
import { UserAvatar } from "@/components/users/user-avatar"

const fieldTypeOptions = [
  { value: "text", label: "Text" },
  { value: "rich_text", label: "Rich text" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Yes/No" },
  { value: "single_select", label: "Single select" },
  { value: "multi_select", label: "Multi select" },
  { value: "user_reference", label: "User reference" },
  { value: "cross_reference", label: "Relationships" },
  { value: "url", label: "URL" },
]

const crossReferenceTargetEnum = z.enum([
  CROSS_REFERENCE_TARGET_OPTIONS[0].value,
  CROSS_REFERENCE_TARGET_OPTIONS[1].value,
  CROSS_REFERENCE_TARGET_OPTIONS[2].value,
  CROSS_REFERENCE_TARGET_OPTIONS[3].value,
  CROSS_REFERENCE_TARGET_OPTIONS[4].value,
])

const formSchema = z.object({
  display_name: z.string().min(1, "Display name is required"),
  field_type: z.enum([
    "text",
    "rich_text",
    "number",
    "date",
    "boolean",
    "single_select",
    "multi_select",
    "user_reference",
    "cross_reference",
    "url",
  ]),
  select_options: z.array(z.string()).optional(),
  cross_reference_allowed_target_types: z
    .array(crossReferenceTargetEnum)
    .min(1, "Select at least one target type")
    .default(CROSS_REFERENCE_TARGET_OPTIONS.map((opt) => opt.value)),
})

type FormValues = z.infer<typeof formSchema>
type UserReferenceMode = "all" | "selected"

interface CreateCustomFieldDialogProps {
  entityType?: string
  onClose: () => void
}

export function CreateCustomFieldDialog({ entityType = "Household", onClose }: CreateCustomFieldDialogProps) {
  const createCustomFieldMutation = useCreateCustomField()
  const dataView = useDataTableStore((state) => state.dataView)
  const columnOptions = useDataTableStore((state) => state.columnOptions)
  const addViewColumn = useDataTableStore((state) => state.addViewColumn)
  const setColumnOptions = useDataTableStore((state) => state.setColumnOptions)
  const [selectOptions, setSelectOptions] = useState<string[]>([])
  const [selectOptionColors, setSelectOptionColors] = useState<Record<string, string>>({})
  const [optionInput, setOptionInput] = useState("")
  const [userReferenceMode, setUserReferenceMode] = useState<UserReferenceMode>("all")
  const [selectedUserIds, setSelectedUserIds] = useState<string[]>([])
  const { data: usersData, isLoading: isLoadingUsers } = useFetchUsers()

  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      display_name: "",
      field_type: "text",
      select_options: [],
      cross_reference_allowed_target_types: CROSS_REFERENCE_TARGET_OPTIONS.map((opt) => opt.value),
    },
  })

  const fieldType = form.watch("field_type")
  const users = useMemo(() => usersData?.users ?? [], [usersData?.users])

  const handleAddOption = () => {
    if (optionInput.trim() && !selectOptions.includes(optionInput.trim())) {
      const newOptions = [...selectOptions, optionInput.trim()]
      setSelectOptions(newOptions)
      form.setValue("select_options", newOptions)
      setOptionInput("")
    }
  }

  const handleRemoveOption = (option: string) => {
    const newOptions = selectOptions.filter((opt) => opt !== option)
    setSelectOptions(newOptions)
    form.setValue("select_options", newOptions)
    setSelectOptionColors((current) => {
      const next = { ...current }
      delete next[option]
      return next
    })
  }

  const toggleSelectedUser = (userId: string) => {
    setSelectedUserIds((current) =>
      current.includes(userId) ? current.filter((id) => id !== userId) : [...current, userId]
    )
  }

  const handleCreateCustomField = (values: FormValues) => {
    if (createCustomFieldMutation.isPending) return
    if (values.field_type === "user_reference" && userReferenceMode === "selected" && selectedUserIds.length === 0) {
      toast.error("Select at least one user or allow all users")
      return
    }

    const buildOptions = () => {
      if (values.field_type === "single_select" || values.field_type === "multi_select") {
        return { select_options: values.select_options, select_option_colors: selectOptionColors }
      }

      if (values.field_type === "cross_reference") {
        return { cross_reference_allowed_target_types: values.cross_reference_allowed_target_types }
      }

      if (values.field_type === "user_reference" && userReferenceMode === "selected") {
        return { user_reference_allowed_user_ids: selectedUserIds }
      }

      return undefined
    }

    const payload = {
      entity_type: entityType,
      display_name: values.display_name,
      field_type: values.field_type,
      options: buildOptions(),
    }

    createCustomFieldMutation.mutate(payload, {
      onSuccess: (data) => {
        toast.success("Custom field created")

        if (dataView?.id && data.custom_field) {
          const fieldKey = data.custom_field.field_key
          const existingColumn = columnOptions.some((column) => column.accessorKey === fieldKey)

          if (!existingColumn) {
            const newColumn = buildCustomFieldColumn(data.custom_field)
            setColumnOptions([...(columnOptions || []), newColumn])
          }

          addViewColumn(fieldKey)
        }

        onClose()
      },
    })
  }

  const formId = useId()
  return (
    <SimpleFormDialog
      open
      onOpenChange={(open) => !open && onClose()}
      title="Create custom field"
      action={{ label: "Create field", form: formId, pending: createCustomFieldMutation.isPending }}
    >
      <Form {...form}>
        <form id={formId} onSubmit={form.handleSubmit(handleCreateCustomField)} className="space-y-2">
          <FormField
            name="display_name"
            render={({ field }) => (
              <TextField
                id="display_name"
                label="Field name"
                placeholder="e.g., Last meeting at"
                field={field}
                error={!!form.formState.errors.display_name?.message}
              />
            )}
          />

          <FormField
            name="field_type"
            render={({ field }) => (
              <SelectField
                id="field_type"
                label="Field type"
                placeholder="Select a field type"
                field={field}
                options={fieldTypeOptions}
              />
            )}
          />

          {(fieldType === "single_select" || fieldType === "multi_select") && (
            <div className="space-y-2">
              <Label>Options</Label>
              <div className="flex gap-2">
                <Input
                  value={optionInput}
                  onChange={(e) => setOptionInput(e.target.value)}
                  placeholder="Enter an option"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault()
                      handleAddOption()
                    }
                  }}
                />
                <Button type="button" onClick={handleAddOption} variant="secondary" size="sm">
                  Add
                </Button>
              </div>
              <div className="flex flex-wrap gap-2">
                {selectOptions.map((option) => (
                  <div key={option} className="bg-secondary flex items-center gap-1 rounded-md px-2 py-1 text-sm">
                    <ColorPicker
                      value={selectOptionColors[option]}
                      onChange={(color) =>
                        setSelectOptionColors((current) => ({
                          ...current,
                          [option]: color,
                        }))
                      }
                      trigger={
                        <button type="button" aria-label={`Set color for ${option}`}>
                          <span
                            className="border-border block size-3 rounded-full border"
                            style={{ backgroundColor: selectOptionColors[option] ?? "var(--color-neutral-100)" }}
                          />
                        </button>
                      }
                    />
                    <span>{option}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveOption(option)}
                      className="text-muted-foreground hover:text-foreground"
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {fieldType === "cross_reference" && (
            <FormField
              name="cross_reference_allowed_target_types"
              render={({ field }) => {
                const selected = (field.value ?? []) as string[]

                return (
                  <div className="space-y-2">
                    <Label>Allowed target types</Label>
                    <div className="space-y-2">
                      {CROSS_REFERENCE_TARGET_OPTIONS.map((option) => {
                        const isSelected = selected.includes(option.value)
                        const toggle = () => {
                          if (isSelected) {
                            if (selected.length === 1) {
                              toast.error("At least one target type must be selected")
                              return
                            }
                            field.onChange(selected.filter((item: string) => item !== option.value))
                          } else {
                            field.onChange([...selected, option.value])
                          }
                        }

                        return (
                          <label key={option.value} className="flex items-center gap-2 text-sm">
                            <input type="checkbox" className="h-4 w-4" checked={isSelected} onChange={toggle} />
                            <span>{option.label}</span>
                          </label>
                        )
                      })}
                    </div>
                    {form.formState.errors.cross_reference_allowed_target_types?.message && (
                      <p className="text-destructive text-sm">
                        {form.formState.errors.cross_reference_allowed_target_types?.message}
                      </p>
                    )}
                  </div>
                )
              }}
            />
          )}

          {fieldType === "user_reference" && (
            <div className="space-y-3">
              <Label>Selectable users</Label>
              <div className="flex gap-2">
                <Button
                  type="button"
                  variant={userReferenceMode === "all" ? "secondary" : "outline"}
                  onClick={() => setUserReferenceMode("all")}
                >
                  All users
                </Button>
                <Button
                  type="button"
                  variant={userReferenceMode === "selected" ? "secondary" : "outline"}
                  onClick={() => setUserReferenceMode("selected")}
                >
                  Selected users
                </Button>
              </div>

              {userReferenceMode === "selected" && (
                <div className="border-border max-h-56 overflow-auto rounded-md border p-2">
                  {isLoadingUsers ? (
                    <div className="text-muted-foreground flex items-center gap-2 p-2 text-sm">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Loading users
                    </div>
                  ) : users.length ? (
                    <div className="space-y-1">
                      {users.map((user) => {
                        const isSelected = selectedUserIds.includes(user.id)

                        return (
                          <label
                            key={user.id}
                            className="hover:bg-muted flex cursor-pointer items-center gap-2 rounded-sm px-2 py-1.5 text-sm"
                          >
                            <Checkbox checked={isSelected} onCheckedChange={() => toggleSelectedUser(user.id)} />
                            <UserAvatar userId={user.id} size="sm" showName />
                          </label>
                        )
                      })}
                    </div>
                  ) : (
                    <div className="text-muted-foreground p-2 text-sm">No users available.</div>
                  )}
                </div>
              )}
              {userReferenceMode === "selected" && selectedUserIds.length === 0 && (
                <p className="text-muted-foreground text-xs">Select at least one user.</p>
              )}
            </div>
          )}
        </form>
      </Form>
    </SimpleFormDialog>
  )
}
